/**
 * SSE client for POST /rag/generate-from-template/stream
 */

export type TemplateStreamEvent = "started" | "progress" | "complete" | "followup" | "heartbeat" | "error";

export type TemplateGenerationPhase =
    | "prep"
    | "understand"
    | "create"
    | "render"
    | "finalize"
    | "done"
    | "error";

export interface TemplateProgressPayload {
    stage: string;
    message: string;
    title?: string;
    detail?: string;
    progress?: number;
    phase?: TemplateGenerationPhase;
    show_preview_skeleton?: boolean;
    session_id?: string;
    template_id?: string;
    template_name?: string;
    media_type?: "image" | "video" | string;
    asset_type?: string;
    estimated_time?: string;
    estimated_seconds?: number;
    elapsed_seconds?: number;
    remaining_seconds?: number;
    tick?: number;
    [key: string]: unknown;
}

/** A single "thinking step" accumulated in the chat bubble during generation. */
export interface StreamStep {
    stage: string;
    title: string;
    message: string;
    phase: TemplateGenerationPhase;
    elapsed_seconds?: number;
    remaining_seconds?: number;
    estimated_time?: string;
    estimated_seconds?: number;
    show_preview_skeleton?: boolean;
    tick?: number;
}

export type TemplateStreamHandler = (
    event: TemplateStreamEvent,
    data: Record<string, unknown>,
) => void;

const STAGE_ICONS: Record<string, string> = {
    started: "⚡",
    quota_check: "📊",
    session_start: "🔑",
    session_ready: "✅",
    ocr_scanning: "🔍",
    parsing: "🧠",
    consolidating: "🔗",
    followup: "💬",
    building_prompt: "✍️",
    generating_image: "🖼️",
    generating_video: "🎬",
    itinerary_render: "📄",
    uploading: "☁️",
    complete: "✅",
    error: "❌",
};

export function getStageIcon(stage: string): string {
    return STAGE_ICONS[stage] ?? "⚙️";
}

/** Map raw SSE JSON into message UI fields. */
export function mapTemplateProgressToMessage(
    data: Record<string, unknown>,
): Pick<
    TemplateProgressPayload,
    "stage" | "message" | "title" | "detail" | "progress" | "phase" | "show_preview_skeleton"
    | "estimated_time" | "estimated_seconds" | "elapsed_seconds" | "remaining_seconds" | "tick"
> & {
    generation_stage: string;
    generation_title: string;
    generation_detail: string;
    generation_progress: number;
    generation_phase: TemplateGenerationPhase;
    generation_status: string;
    show_generation_skeleton: boolean;
    generation_media_type?: string;
    generation_template_name?: string;
    elapsed_seconds?: number;
    remaining_seconds?: number;
    estimated_time?: string;
    estimated_seconds?: number;
    tick?: number;
} {
    const stage = String(data.stage ?? "progress");
    const title = String(data.title ?? data.message ?? "Working…");
    const detail = String(data.detail ?? "");
    const message = String(data.message ?? title);
    const progress = typeof data.progress === "number" ? data.progress : 0;
    const phase = (data.phase as TemplateGenerationPhase) || "prep";
    const showSkeleton = Boolean(
        data.show_preview_skeleton
        || stage === "generating_image"
        || stage === "generating_video"
        || stage === "itinerary_render",
    );
    const elapsed_seconds = typeof data.elapsed_seconds === "number" ? data.elapsed_seconds : undefined;
    const remaining_seconds = typeof data.remaining_seconds === "number" ? data.remaining_seconds : undefined;
    const estimated_time = typeof data.estimated_time === "string" ? data.estimated_time : undefined;
    const estimated_seconds = typeof data.estimated_seconds === "number" ? data.estimated_seconds : undefined;
    const tick = typeof data.tick === "number" ? data.tick : undefined;

    return {
        stage,
        message,
        title,
        detail,
        progress,
        phase,
        show_preview_skeleton: showSkeleton,
        estimated_time,
        estimated_seconds,
        elapsed_seconds,
        remaining_seconds,
        tick,
        generation_stage: stage,
        generation_title: title,
        generation_detail: detail,
        generation_progress: progress,
        generation_phase: phase,
        generation_status: detail ? `${title} — ${detail}` : title,
        show_generation_skeleton: showSkeleton,
        generation_media_type: data.media_type as string | undefined,
        generation_template_name: data.template_name as string | undefined,
    };
}

/**
 * Merge a new event into an accumulated steps array.
 * - Same stage as last step (ticker tick) → update in-place (elapsed/remaining/message)
 * - New stage → append
 */
export function mergeStreamStep(
    steps: StreamStep[],
    mapped: ReturnType<typeof mapTemplateProgressToMessage>,
): StreamStep[] {
    const incoming: StreamStep = {
        stage: mapped.generation_stage,
        title: mapped.generation_title,
        message: mapped.message ?? mapped.generation_title,
        phase: mapped.generation_phase,
        elapsed_seconds: mapped.elapsed_seconds,
        remaining_seconds: mapped.remaining_seconds,
        estimated_time: mapped.estimated_time,
        estimated_seconds: mapped.estimated_seconds,
        show_preview_skeleton: mapped.show_generation_skeleton,
        tick: mapped.tick,
    };

    if (steps.length === 0) return [incoming];

    const last = steps[steps.length - 1];
    if (last.stage === incoming.stage) {
        // Same stage — update the last entry (handles render ticker ticks)
        return [...steps.slice(0, -1), { ...last, ...incoming }];
    }
    return [...steps, incoming];
}

export function parseSseChunk(buffer: string): { events: Array<{ event: string; data: string }>; rest: string } {
    const events: Array<{ event: string; data: string }> = [];
    const parts = buffer.split("\n\n");
    const rest = parts.pop() ?? "";
    for (const block of parts) {
        if (!block.trim()) continue;
        let event = "progress";
        let data = "";
        for (const line of block.split("\n")) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            if (line.startsWith("data:")) data += line.slice(5).trim();
        }
        if (data) events.push({ event, data });
    }
    return { events, rest };
}

export function normalizeStreamEvent(name: string): TemplateStreamEvent {
    if (name === "started" || name === "complete" || name === "error"
        || name === "followup" || name === "heartbeat") return name;
    return "progress";
}

export async function streamGenerateFromTemplate(
    url: string,
    headers: Record<string, string>,
    body: Record<string, unknown>,
    onEvent: TemplateStreamHandler,
    _files?: File[],
): Promise<void> {
    const res = await fetch(url, {
        method: "POST",
        headers: {
            ...headers,
            "Content-Type": "application/json",
            Accept: "text/event-stream",
        },
        body: JSON.stringify(body),
    });

    if (!res.ok) {
        // Never surface a raw status code to the user — reads as a broken app.
        const FRIENDLY_FALLBACK = "We are experiencing high volume right now. Please try again in a moment.";
        let detail: Record<string, unknown> = { message: FRIENDLY_FALLBACK };
        try {
            const json = await res.json();
            const parsed = (json.detail as Record<string, unknown>) ?? json;
            if (parsed && typeof parsed === "object" && typeof parsed.message === "string" && parsed.message.trim()) {
                detail = parsed;
            }
        } catch {
            /* ignore — keep friendly fallback */
        }
        // subscription_error_handler (backend) keys the quota code as `error`,
        // not `code` — normalize so callers reading detail_payload.code (e.g.
        // GeneratorPage's ErrorBanner) see DAILY_QUOTA_EXCEEDED/MONTHLY_QUOTA_EXCEEDED
        // the same way the axios-based session endpoints already do via extractQuotaError.
        const rawCode =
            (typeof detail.code === "string" && detail.code) ||
            (typeof detail.error === "string" && detail.error) ||
            "";
        if (detail.code == null && rawCode) {
            detail = { ...detail, code: rawCode };
        }
        // Same detection RagChatPage's maybeEmitQuotaFromStream/apiErrorResponse use:
        // the error code is the source of truth for "is this a quota wall", not the
        // raw HTTP status — go by the code (or message text) it actually carries.
        const isQuota = rawCode ? /quota/i.test(rawCode) : /quota|limit/i.test(String(detail.message ?? ""));
        onEvent("error", {
            stage: "error",
            title: isQuota ? "Daily limit reached ✋" : "System is busy",
            message: String(detail.message ?? FRIENDLY_FALLBACK),
            detail: String(detail.message ?? FRIENDLY_FALLBACK),
            detail_payload: detail,
            status: res.status,
            progress: 0,
            phase: "done",
        });
        return;
    }

    const reader = res.body?.getReader();
    if (!reader) {
        onEvent("error", {
            stage: "error",
            title: "System is busy",
            message: "We are experiencing high volume right now. Please try again in a moment.",
            progress: 0,
            phase: "done",
        });
        return;
    }

    const decoder = new TextDecoder();
    let buffer = "";

    const dispatch = (ev: { event: string; data: string }) => {
        try {
            const data = JSON.parse(ev.data) as Record<string, unknown>;
            onEvent(normalizeStreamEvent(ev.event), data);
        } catch {
            /* skip malformed */
        }
    };

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseSseChunk(buffer);
        buffer = rest;
        for (const ev of events) dispatch(ev);
    }

    if (buffer.trim()) {
        const { events } = parseSseChunk(buffer + "\n\n");
        for (const ev of events) dispatch(ev);
    }
}
